/**
 * Bots — the Bitcoin legends as Q3-style opponents.
 * Articulated low-poly humanoid (torso, head+visor, shoulders, arms, legs, gun),
 * per-legend color scheme, name tag sprite. AI: patrol between navigation points
 * (items/spawns/platform centers), engage on sight, strafe, jump, aim with error.
 */

import * as THREE from 'three';
import { QuakePlayer, MoveInput } from './movement';
import { Hittable } from './weapons';
import { LightningYard } from './map';

export interface LegendDef {
  id: string;
  name: string;
  title: string;
  primary: number;   // suit
  accent: number;    // visor / trims
  skill: number;     // 0..1 aim + reaction
  taunt: string[];
  killLine: string[];
}

export const LEGENDS: LegendDef[] = [
  { id: 'adam-back', name: 'Adam Back', title: 'Hashcash', primary: 0x0e3a3a, accent: 0x00ffff, skill: 0.8, taunt: ['Proof of work, or nothing.'], killLine: ['Difficulty adjusted.'] },
  { id: 'saylor', name: 'Michael Saylor', title: 'Orange Laser', primary: 0x3a2008, accent: 0xff8800, skill: 0.85, taunt: ['There is no second best.'], killLine: ['You will never sell.'] },
  { id: 'hal-finney', name: 'Hal Finney', title: 'Running bitcoin', primary: 0x0d3324, accent: 0x00ff88, skill: 0.7, taunt: ['Running bitcoin.'], killLine: ['Reusable proof of work.'] },
  { id: 'szabo', name: 'Nick Szabo', title: 'Bit Gold', primary: 0x0b1e3a, accent: 0x4488ff, skill: 0.75, taunt: ['Trusted third parties are security holes.'], killLine: ['Contract executed.'] },
  { id: 'wei-dai', name: 'Wei Dai', title: 'b-money', primary: 0x0b2a2a, accent: 0x00cccc, skill: 0.65, taunt: ['Untraceable.'], killLine: ['Minted.'] },
  { id: 'luke', name: 'Luke Dashjr', title: 'Consensus', primary: 0x3a3308, accent: 0xffcc00, skill: 0.7, taunt: ['Invalid block.'], killLine: ['Consensus enforced.'] },
  { id: 'calle', name: 'Calle', title: 'Cashu', primary: 0x3a1508, accent: 0xff4422, skill: 0.7, taunt: ['Ecash never forgets.'], killLine: ['Token burned.'] },
  { id: 'lagarde', name: 'Christine Lagarde', title: 'THE BOSS', primary: 0x2a0814, accent: 0xff2266, skill: 0.95, taunt: ['Cash is going away.'], killLine: ['Your purchasing power is gone.'] },
];

// ---------------------------------------------------------------- model

export function buildLegendModel(def: LegendDef): THREE.Group {
  const g = new THREE.Group();
  g.name = `Legend_${def.id}`;

  const suit = new THREE.MeshStandardMaterial({ color: def.primary, roughness: 0.55, metalness: 0.35 });
  const armor = new THREE.MeshStandardMaterial({ color: 0x1a1a22, roughness: 0.35, metalness: 0.85 });
  const glow = new THREE.MeshStandardMaterial({ color: 0x000000, emissive: def.accent, emissiveIntensity: 3, roughness: 0.3 });
  const skin = new THREE.MeshStandardMaterial({ color: 0xc9a98a, roughness: 0.8, metalness: 0 });

  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, name?: string) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    m.castShadow = true; m.receiveShadow = true;
    if (name) m.name = name;
    g.add(m);
    return m;
  };

  // Legs
  add(new THREE.CapsuleGeometry(0.11, 0.55, 4, 8), suit, -0.15, 0.42, 0, 'legL');
  add(new THREE.CapsuleGeometry(0.11, 0.55, 4, 8), suit, 0.15, 0.42, 0, 'legR');
  add(new THREE.BoxGeometry(0.16, 0.1, 0.28), armor, -0.15, 0.06, 0.03);
  add(new THREE.BoxGeometry(0.16, 0.1, 0.28), armor, 0.15, 0.06, 0.03);
  // Hips / belt with glow strip
  add(new THREE.BoxGeometry(0.44, 0.16, 0.3), armor, 0, 0.82, 0);
  add(new THREE.BoxGeometry(0.46, 0.03, 0.31), glow, 0, 0.9, 0);
  // Torso (chest armor)
  const torso = add(new THREE.BoxGeometry(0.5, 0.55, 0.32), suit, 0, 1.2, 0, 'torso');
  add(new THREE.BoxGeometry(0.42, 0.34, 0.1), armor, 0, 1.22, 0.18);
  add(new THREE.BoxGeometry(0.28, 0.05, 0.02), glow, 0, 1.3, 0.24); // chest light
  // Shoulders (pauldrons)
  add(new THREE.SphereGeometry(0.15, 10, 8), armor, -0.34, 1.42, 0);
  add(new THREE.SphereGeometry(0.15, 10, 8), armor, 0.34, 1.42, 0);
  // Arms
  add(new THREE.CapsuleGeometry(0.08, 0.45, 4, 8), suit, -0.38, 1.05, 0, 'armL');
  const armR = add(new THREE.CapsuleGeometry(0.08, 0.45, 4, 8), suit, 0.38, 1.05, 0.1, 'armR');
  armR.rotation.x = -0.9;
  // Neck + head
  add(new THREE.CylinderGeometry(0.07, 0.09, 0.1, 8), skin, 0, 1.52, 0);
  add(new THREE.SphereGeometry(0.19, 14, 12), skin, 0, 1.7, 0, 'head');
  // Helmet band + visor (the Lightning Mask)
  add(new THREE.CylinderGeometry(0.2, 0.2, 0.12, 14, 1, true), armor, 0, 1.76, 0);
  const visor = add(new THREE.BoxGeometry(0.3, 0.09, 0.08), glow, 0, 1.72, 0.17, 'visor');
  visor.rotation.x = 0.1;
  // Gun (held) — lightning gun taille tierce personne, accents de la légende
  const gun = new THREE.Group();
  gun.name = 'gun';
  const gunMetal = new THREE.MeshStandardMaterial({ color: 0x24262e, roughness: 0.34, metalness: 0.92 });
  const gb = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.15, 0.62), gunMetal);
  const gtop = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.045, 0.4), armor);
  gtop.position.set(0, 0.09, -0.05);
  const gbarrel = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.042, 0.55, 10), gunMetal);
  gbarrel.rotation.x = Math.PI / 2; gbarrel.position.z = -0.52;
  const gcoil1 = new THREE.Mesh(new THREE.TorusGeometry(0.072, 0.016, 6, 14), glow);
  gcoil1.position.z = -0.45;
  const gcoil2 = new THREE.Mesh(new THREE.TorusGeometry(0.066, 0.014, 6, 14), armor);
  gcoil2.position.z = -0.62;
  const gtip = new THREE.Mesh(new THREE.TorusGeometry(0.058, 0.014, 6, 14), glow);
  gtip.position.z = -0.78;
  const ggrip = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.2, 0.09), armor);
  ggrip.position.set(0, -0.16, 0.16); ggrip.rotation.x = 0.25;
  const gcell = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.18, 8), glow);
  gcell.rotation.x = Math.PI / 2; gcell.position.set(-0.09, -0.03, 0.05);
  // point de sortie du canon : origine réelle de l'éclair (plus « œil − 0,2 m »)
  const gmuzzle = new THREE.Object3D();
  gmuzzle.name = 'muzzle';
  gmuzzle.position.set(0, 0, -0.86);
  gun.add(gb, gtop, gbarrel, gcoil1, gcoil2, gtip, ggrip, gcell, gmuzzle);
  gun.position.set(0.34, 1.13, -0.3);
  g.add(gun);

  // Name tag sprite
  const canvas = document.createElement('canvas');
  canvas.width = 512; canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  ctx.font = 'bold 52px ui-monospace, Menlo, monospace';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.shadowColor = '#' + def.accent.toString(16).padStart(6, '0'); ctx.shadowBlur = 18;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(def.name, 256, 48);
  ctx.font = '30px ui-monospace, Menlo, monospace';
  ctx.fillStyle = '#' + def.accent.toString(16).padStart(6, '0');
  ctx.fillText(def.title, 256, 98);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const tag = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
  tag.scale.set(2.4, 0.6, 1);
  tag.position.set(0, 2.25, 0);
  tag.name = 'tag';
  g.add(tag);

  return g;
}

// ---------------------------------------------------------------- AI

export type BotState = 'roam' | 'hunt' | 'dead';

/**
 * Combien de bots ont déjà pris le joueur pour cible dans la frame courante.
 * Remis à zéro par le jeu avant la boucle d'IA (voir `resetPlayerFocus`).
 */
let playerFocus = 0;
export function resetPlayerFocus(): void { playerFocus = 0; }

export interface DamageEvent {
  attackerId: string;
  amount: number;
}

export class Bot implements Hittable {
  public readonly id: string;
  public readonly def: LegendDef;
  public readonly model: THREE.Group;
  public readonly mover = new QuakePlayer();
  public health = 100;
  public armor = 0;
  public state: BotState = 'roam';
  public frags = 0;
  public deaths = 0;
  public respawnAt = 0;

  private target: THREE.Vector3 | null = null;
  private retargetAt = 0;
  private strafeDir = 1;
  private strafeSwitchAt = 0;
  private jumpAt = 0;
  private fireCooldown = 0;
  private aimError = new THREE.Vector3();
  private aimErrorAt = 0;
  private legPhase = 0;
  private tmp = new THREE.Vector3();
  private tmp2 = new THREE.Vector3();
  /** temporaires dédiés : réutiliser tmp2 écrasait la direction de tir et la cible */
  private fireDir = new THREE.Vector3();
  private targetEyeV = new THREE.Vector3();
  private aimAtV = new THREE.Vector3();
  // rafales : le bot tire par salves puis marque une pause (TTK jouable)
  private burstEnd = 0;
  private burstPause = 0;
  /** horodatage du dernier éclair émis (le HUD/jeu masque le bolt après 80 ms) */
  public lastBoltAt = 0;
  private probeDir = new THREE.Vector3();
  private fireOrigin = new THREE.Vector3();
  private gun: THREE.Object3D | null = null;
  private muzzle: THREE.Object3D | null = null;
  /** pièces animées résolues une fois : `getObjectByName` parcourait tout le
   *  modèle 6 fois par bot et par frame (30 traversées d'arbre à 60 fps) */
  private legL: THREE.Object3D | null = null;
  private legR: THREE.Object3D | null = null;
  private armL: THREE.Object3D | null = null;
  private head: THREE.Object3D | null = null;
  private torso: THREE.Object3D | null = null;
  /** entrée de mouvement réutilisée (un objet par bot et par frame auparavant) */
  private readonly input: MoveInput = { forward: 0, right: 0, jump: false, crouch: false };

  constructor(def: LegendDef, scene: THREE.Scene) {
    this.id = def.id;
    this.def = def;
    this.model = buildLegendModel(def);
    this.gun = this.model.getObjectByName('gun') ?? null;
    this.muzzle = this.gun?.getObjectByName('muzzle') ?? null;
    this.legL = this.model.getObjectByName('legL') ?? null;
    this.legR = this.model.getObjectByName('legR') ?? null;
    this.armL = this.model.getObjectByName('armL') ?? null;
    this.head = this.model.getObjectByName('head') ?? null;
    this.torso = this.model.getObjectByName('torso') ?? null;
    if (def.id === 'lagarde') { this.model.scale.setScalar(1.25); this.health = 200; this.armor = 100; }
    scene.add(this.model);
  }

  /** Position monde du bout du canon (origine de l'éclair). */
  muzzleWorld(out: THREE.Vector3): THREE.Vector3 {
    if (!this.muzzle) return this.mover.eye(out);
    this.muzzle.updateWorldMatrix(true, false);
    return out.setFromMatrixPosition(this.muzzle.matrixWorld);
  }

  alive(): boolean { return this.state !== 'dead'; }

  hitBox(out: { min: THREE.Vector3; max: THREE.Vector3 }): void {
    const p = this.mover.position;
    const s = this.model.scale.x;
    out.min.set(p.x - 0.42 * s, p.y, p.z - 0.42 * s);
    out.max.set(p.x + 0.42 * s, p.y + 1.95 * s, p.z + 0.42 * s);
  }

  spawn(map: LightningYard, avoid?: THREE.Vector3 | THREE.Vector3[]): void {
    const s = map.randomSpawn(avoid);
    this.mover.teleport(s.position, s.yaw);
    this.health = this.def.id === 'lagarde' ? 200 : 100;
    this.armor = this.def.id === 'lagarde' ? 100 : 0;
    this.state = 'roam';
    this.model.visible = true;
    this.target = null;
    this.fireCooldown = 1.0;
    this.burstEnd = 0;
    this.burstPause = 0;
  }

  /** Apply damage; returns true if this killed the bot */
  damage(amount: number): boolean {
    if (!this.alive()) return false;
    // Q3 armor: absorbs 2/3
    const absorbed = Math.min(this.armor, Math.floor(amount * 0.66));
    this.armor -= absorbed;
    this.health -= amount - absorbed;
    if (this.health <= 0) {
      this.state = 'dead';
      this.deaths++;
      this.model.visible = false;
      this.respawnAt = performance.now() + 2500;
      return true;
    }
    return false;
  }

  /**
   * AI tick. Returns a fire request (origin, dir) when the bot decides to shoot.
   * `others` = alive opponents (other bots), `player` = the human.
   */
  think(
    dt: number,
    map: LightningYard,
    player: { position: THREE.Vector3; alive: boolean; eye: THREE.Vector3 },
    others: Bot[],
    canSee: (from: THREE.Vector3, to: THREE.Vector3) => boolean,
  ): { origin: THREE.Vector3; dir: THREE.Vector3 } | null {
    if (!this.alive()) return null;
    const now = performance.now();
    const pos = this.mover.position;
    const eye = this.mover.eye(this.tmp);

    // Perception — nearest visible enemy wins.
    // La position de l'œil visé est COPIÉE dans un temporaire dédié : la
    // réutilisation de tmp2 dans la boucle écrasait la cible retenue.
    let targetEye: THREE.Vector3 | null = null;
    let targetDist = 61;
    let targetIsPlayer = false;
    if (player.alive) {
      const d = eye.distanceTo(player.eye);
      if (d < targetDist && canSee(eye, player.eye)) { this.targetEyeV.copy(player.eye); targetEye = this.targetEyeV; targetDist = d; targetIsPlayer = true; }
    }
    for (const o of others) {
      if (o === this || !o.alive()) continue;
      const oe = o.mover.eye(this.tmp2);
      const d = eye.distanceTo(oe);
      if (d < targetDist && canSee(eye, oe)) { this.targetEyeV.copy(oe); targetEye = this.targetEyeV; targetDist = d; targetIsPlayer = false; }
    }
    if (targetEye) this.state = 'hunt';
    else if (this.state === 'hunt' && now > this.retargetAt) this.state = 'roam';

    let fire: { origin: THREE.Vector3; dir: THREE.Vector3 } | null = null;
    const input = this.input;
    input.forward = 0; input.right = 0; input.jump = false; input.crouch = false;

    if (this.state === 'hunt' && targetEye) {
      this.retargetAt = now + 2500;
      // Face target with aim error scaled by skill
      // Un seul bot « tient » le joueur correctement : les suivants visent 30 %
      // moins bien (sinon 2 bots = mort instantanée).
      const secondary = targetIsPlayer && playerFocus++ > 0;
      if (now > this.aimErrorAt) {
        const err = (1 - this.def.skill) * 1.8 * (secondary ? 1.3 : 1);
        this.aimError.set((Math.random() - 0.5) * err, (Math.random() - 0.5) * err * 0.6, (Math.random() - 0.5) * err);
        this.aimErrorAt = now + 300 + Math.random() * 200;
      }
      const aimAt = this.aimAtV.copy(targetEye).add(this.aimError);
      const dx = aimAt.x - eye.x, dy = aimAt.y - eye.y, dz = aimAt.z - eye.z;
      const targetYaw = Math.atan2(-dx, -dz);
      const targetPitch = Math.atan2(dy, Math.hypot(dx, dz));
      // turn rate — monotone convergence regardless of frame rate
      const maxTurn = 6 * (0.5 + this.def.skill) * dt;
      const dyaw = this.wrapAngle(targetYaw - this.mover.yaw);
      const dpitch = targetPitch - this.mover.pitch;
      // never overshoot: cap at 55% of remaining angle (or the max rate)
      const capYaw = Math.min(maxTurn, Math.abs(dyaw) * 0.55 + 0.001);
      const capPitch = Math.min(maxTurn, Math.abs(dpitch) * 0.55 + 0.001);
      this.mover.yaw += Math.sign(dyaw) * capYaw;
      this.mover.pitch += Math.sign(dpitch) * capPitch;

      // Movement: keep mid range, strafe, hop
      const dist = Math.hypot(dx, dz);
      input.forward = dist > 14 ? 1 : dist < 7 ? -0.6 : 0;
      if (now > this.strafeSwitchAt) { this.strafeDir *= -1; this.strafeSwitchAt = now + 600 + Math.random() * 900; }
      input.right = this.strafeDir * 0.75; // Q3 bots strafe but not at full speed
      if (now > this.jumpAt && Math.random() < 0.02) { input.jump = true; this.jumpAt = now + 700; }

      // Fire — LG is a beam weapon: generous alignment window + LOS already verified.
      // Rafales : 0,8-1,4 s de tir puis 0,7-1,2 s de pause → le joueur peut
      // se replier (≈2,5-3 s de survie face à 1 bot à découvert).
      if (this.burstEnd === 0) {
        if (now >= this.burstPause) this.burstEnd = now + 800 + Math.random() * 600;
      } else if (now > this.burstEnd) {
        this.burstEnd = 0;
        this.burstPause = now + 700 + Math.random() * 500;
      }
      this.fireCooldown -= dt;
      const alignYaw = Math.abs(this.wrapAngle(targetYaw - this.mover.yaw));
      const alignPitch = Math.abs(targetPitch - this.mover.pitch);
      if (this.burstEnd > 0 && this.fireCooldown <= 0 && alignYaw < 0.45 && alignPitch < 0.45) {
        // Fire at the exact aim point (target + skill error) — like real Q3 bots
        const origin = this.muzzleWorld(this.fireOrigin);
        const dir = this.fireDir.copy(aimAt).sub(origin).normalize();
        fire = { origin, dir };
        this.fireCooldown = 0.08; // LG tick rate bot (≈100 dps en rafale)
      }
    } else {
      // Sur le catch deck : cap direct sur le pad de retour le plus proche,
      // sinon le bot tourne en rond autour des items de consolation.
      if (pos.y < map.deckY + 2) {
        if (!this.target || now > this.retargetAt) {
          let best: THREE.Vector3 | null = null, bestD = Infinity;
          for (const pad of map.jumpPads) {
            if (pad.position.y > map.deckY + 1) continue;
            const d = pad.position.distanceToSquared(pos);
            if (d < bestD) { bestD = d; best = pad.position; }
          }
          if (best) { this.target = best.clone(); this.retargetAt = now + 1500; }
        }
      } else if (!this.target || pos.distanceTo(this.target) < 2 || now > this.retargetAt) {
        // Roam: pick a navigation target (items, spawns or jump pads), walk to it
        const candidates = [
          ...map.items.filter(i => !i.respawnAt).map(i => i.position),
          ...map.spawns.map(sp => sp.position),
          ...map.jumpPads.map(pd => pd.position),
        ];
        const near = candidates.filter(c => Math.abs(c.y - pos.y) < 6 && c.distanceToSquared(pos) > 16);
        const pool = near.length ? near : candidates;
        this.target = pool[Math.floor(Math.random() * pool.length)].clone();
        this.retargetAt = now + 6000;
      }
      if (this.target) {
        const dx = this.target.x - pos.x, dz = this.target.z - pos.z;
        const targetYaw = Math.atan2(-dx, -dz);
        this.mover.yaw += THREE.MathUtils.clamp(this.wrapAngle(targetYaw - this.mover.yaw), -4 * dt, 4 * dt);
      }
      this.mover.pitch *= 0.9;
      input.forward = 1;
      // hop occasionally like a Q3 bot
      if (now > this.jumpAt && Math.random() < 0.01) { input.jump = true; this.jumpAt = now + 1500; }
    }

    // Avoid walking off edges: probe ahead; if nothing below, jump (or turn)
    const f = this.probeDir.set(-Math.sin(this.mover.yaw), 0, -Math.cos(this.mover.yaw));
    const probe = this.tmp.copy(pos).addScaledVector(f, 1.6);
    if (!this.groundBelow(probe, map)) {
      if (this.state === 'hunt') { input.forward = Math.min(input.forward, 0); input.right = this.strafeDir; }
      else { this.mover.yaw += Math.PI * 0.5; this.target = null; }
    }

    this.mover.update(dt, input, map);

    // Fell into the void — damage(9999) incrémente déjà `deaths`
    if (pos.y < map.killZ) this.damage(9999);

    // Sync model
    this.model.position.copy(pos);
    this.model.rotation.y = this.mover.yaw;
    this.animate(dt, input.forward !== 0 || input.right !== 0);
    return fire;
  }

  private animate(dt: number, moving: boolean): void {
    const speed = this.mover.speed2D();
    this.legPhase += dt * (moving ? Math.max(6, speed * 1.4) : 0);
    const swing = moving ? Math.sin(this.legPhase) * 0.6 : 0;
    if (this.legL) this.legL.rotation.x = swing;
    if (this.legR) this.legR.rotation.x = -swing;
    if (this.armL) this.armL.rotation.x = -swing * 0.7;
    if (this.head) this.head.rotation.x = -this.mover.pitch * 0.5;
    // le canon suit la visée (l'éclair part du bout du canon, pas de l'œil)
    if (this.gun) this.gun.rotation.x = this.mover.pitch;
    // bob
    if (this.torso) this.torso.position.y = 1.2 + (moving ? Math.abs(Math.sin(this.legPhase * 2)) * 0.03 : 0);
  }

  private groundBelow(p: THREE.Vector3, map: LightningYard): boolean {
    for (const s of map.solidsAt(p.x, p.z)) {
      if (p.x >= s.min.x - 0.2 && p.x <= s.max.x + 0.2 && p.z >= s.min.z - 0.2 && p.z <= s.max.z + 0.2 && s.max.y <= p.y + 0.6 && s.max.y >= p.y - 3.5) return true;
    }
    return false;
  }

  private wrapAngle(a: number): number {
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    return a;
  }
}